import { useHeaderStore } from '@issuemanager/apps/client/contexts';
import { useAppConfig } from '@issuemanager/apps/client/hooks';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { IncompatibilityError } from './components/incompatibility-error';
import { IssuesGridWrapper } from './components/issues-grid-wrapper';
import { VersionSelector } from './components/version-selector';
import { useComponentVersionSelection } from './hooks/use-selected-version';

export default function SharedComponentsTest(): React.JSX.Element {
  const { setHeaderProps } = useHeaderStore();
  const appConfig = useAppConfig();

  const {
    selectedVersion,
    setSelectedVersion,
    currentVersionOption,
    availableVersions,
  } = useComponentVersionSelection();

  useEffect(() => {
    setHeaderProps({
      viewTitle: { title: 'Shared Components Test' },
    });
  }, [setHeaderProps]);

  return (
    <Box px={4} py={4}>
      <Stack spacing={4}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant='h5' gutterBottom>
              Shared Components Test Page
            </Typography>
            <Alert severity='info'>
              Validate different versions of the shared Issues grid component
              with real API data.
            </Alert>
            <Typography variant='body2' color='text.secondary'>
              <strong>Package:</strong>
              {' @diligentcorp/issue-management-shared-components-issues'}
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant='h6' gutterBottom>
              Select Component Version
            </Typography>
            <VersionSelector
              selectedVersion={selectedVersion}
              availableVersions={availableVersions}
              onVersionChange={setSelectedVersion}
            />
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant='h6' gutterBottom>
              Issues Grid - Version
              {` ${currentVersionOption.metadata.version}`}
            </Typography>
            {currentVersionOption.metadata.incompatible ? (
              <>
                <Alert severity='warning' sx={{ mb: 2 }}>
                  This version is not compatible with the current application
                  environment.
                </Alert>
                <IncompatibilityError
                  metadata={currentVersionOption.metadata}
                />
              </>
            ) : (
              currentVersionOption.component && (
                <IssuesGridWrapper
                  key={selectedVersion}
                  IssuesComponent={currentVersionOption.component}
                  apiUrl={appConfig.apiUrl}
                />
              )
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
